<template>
<div class="user-details-container">
    <!-- Refresh button -->
    <div class="refresh-container">
        <a href="#" class="brand w-nav-brand"><img sizes="84.03125px" alt="" :src="logo" loading="lazy" class="image-389" style="height: 45px;"></a>
        <button @click="fetchUserDetails(user ? user.id : $route.params.id)" class="btn-refresh md-btn md-btn-primary">
            <i class="material-icons">refresh</i> Refresh
        </button>
    </div>

    <div v-if="loading" class="loading">
        <div class="md-loader"></div>
        <p>Loading user details...</p>
    </div>

    <div v-else-if="error" class="error md-card md-card-error">
        <i class="material-icons">error</i>
        <p>{{ error }}</p>
    </div>

    <div v-else-if="user" class="main-content">
        <!-- Left Column: User Details and Cart -->
        <div class="left-column">
            <div class="user-card md-card">
                <h2 class="md-card-title">{{AppName}} - User Profile</h2>

                <div class="user-info">
                    <div class="info-row">
                        <span class="label">Name:</span>
                        <span class="value">{{ user.first_name }} {{ user.last_name }}</span>
                    </div>

                    <div class="info-row">
                        <span class="label">Email:</span>
                        <span class="value">{{ user.email }}</span>
                    </div>

                    <div v-if="user.phone" class="info-row">
                        <span class="label">Phone:</span>
                        <span class="value">{{ user.phone }}</span>
                    </div>

                    <div v-if="user.gender" class="info-row">
                        <span class="label">Gender:</span>
                        <span class="value">{{ user.gender }}</span>
                    </div>

                    <div v-if="user.dob" class="info-row">
                        <span class="label">Date of Birth:</span>
                        <span class="value">{{ user.dob }}</span>
                    </div>

                    <div v-if="user.weight" class="info-row">
                        <span class="label">Weight:</span>
                        <span class="value">{{ user.weight }}</span>
                    </div>
                    <div v-if="user.desired_weight" class="info-row">
                        <span class="label">Desired Weight:</span>
                        <span class="value">{{ user.desired_weight }}</span>
                    </div>

                    <div v-if="user.height_inch" class="info-row">
                        <span class="label">Height (inches):</span>
                        <span class="value">{{ user.height_inch }}</span>
                    </div>
                </div>
            </div>

            <!-- Cart Information Section -->
            <div class="cart-section md-card">
                <h3 class="md-card-title">Cart Information</h3>

                <!-- Shown whether or not a cart exists. It used to be v-if="cart", so an
                     agent could only manage a cart the customer had already built —
                     exactly backwards for a win-back call, where there is no cart and
                     the agent is the one creating it. /update-cart already creates one
                     from "no_product_found"; only this template was hiding the button. -->
                <div class="current-product md-panel">
                    <p v-if="cart"><b>Current Product:</b> {{ cart.cart_items.name }}</p>
                    <p v-else class="cart-empty">No cart yet — set a product first, then the link can be sent.</p>
                    <div class="cart-actions">
                        <button @click="showUpdateCartDialog = true" class="btn-update-cart md-btn md-btn-success">
                            <i class="material-icons">edit</i> {{ cart ? 'Update Cart Product' : 'Set Cart Product' }}
                        </button>
                        <!-- Requires a real cart: the link is /weightlosspayment/<cartId>,
                             so sending without one texts the customer a dead page. -->
                        <button @click="sendCartLinkSMS" class="btn-send-link md-btn md-btn-info"
                                :disabled="sendingLink || !cart"
                                :title="!cart ? 'Set a cart product first' : ''">
                            <i class="material-icons">{{ sendingLink ? 'hourglass_empty' : 'send' }}</i>
                            {{ sendingLink ? 'Sending...' : 'Send Cart Link' }}
                        </button>
                    </div>

                    <!-- SMS Status Message -->
                    <div v-if="smsStatus" class="sms-status" :class="{'sms-success': smsStatus === 'success', 'sms-error': smsStatus === 'error'}">
                        <i class="material-icons">{{ smsStatus === 'success' ? 'check_circle' : 'error' }}</i>
                        <span>{{ smsStatusMessage }}</span>
                    </div>
                </div>

                <!-- The backend decides whether a new signup would go through, and says
                     why it would not. Gating on the orders array instead (the old rule)
                     hid this panel for anyone with a past delivered order, so a churned
                     customer could never be re-sold. -->
                <div v-if="!chargeable" class="order-exists md-alert md-alert-success">
                    <i class="material-icons">check_circle</i>
                    <div>
                        <p><b>{{ blockedTitle }}</b></p>
                        <p>{{ blockedMessage }}</p>
                    </div>
                </div>

                <!-- Show cart contents -->
                <div v-else class="current-product md-panel">
                    <h3 class="md-card-subtitle">Charge User</h3>
                    <!-- The Stripe card row sits between two always-rendered groups on purpose.
                         Every v-if row lives inside a group, so the card row is a stable child of
                         this panel and Vue never reuses or moves its element. When conditional rows
                         changed on BOTH sides of the card at once (price/free add-on above, add-ons
                         below, all appearing on plan pick), Vue's keyless diff patched another row
                         into the card's element and wiped the mounted Stripe iframe. -->
                    <div class="charge-group">
                        <div class="info-row">
                            <span class="label">Select Product</span>
                            <span class="value">
                                <select v-model="selectedProductId" @change="updateSelectedProduct">
                                    <option value="">Choose a product</option>
                                    <option v-for="p in productOptions" :key="p.id" :value="p.id">
                                        {{ p.label }}
                                    </option>
                                </select>
                            </span>
                        </div>
                        <div class="info-row">
                            <span class="label">Select Plan</span>
                            <span class="value">
                                <select v-model="selectedPlanKey" @change="updatePlanAndPrice">
                                    <option value="">Choose a plan</option>
                                    <option v-for="opt in planOptions" :key="opt.key" :value="opt.key">
                                        {{ opt.label }}
                                    </option>
                                </select>
                            </span>
                        </div>
                        <!-- Med -> plan -> OFFER. Only live offers covering this product at this
                             plan length are listed, so the agent is never shown a giveaway that
                             would come back as a charge. -->
                        <div v-if="qualifyingOffers.length" class="info-row">
                            <span class="label">Offer</span>
                            <span class="value">
                                <select v-model="selectedOfferSlug" @change="onOfferChange">
                                    <option value="">None</option>
                                    <option v-for="offer in qualifyingOffers" :key="offer.slug" :value="offer.slug">
                                        {{ offer.name }}
                                    </option>
                                </select>
                            </span>
                        </div>
                        <!-- Free for life (free-nad-nasal-life, free-nad-injection-life): the
                             offer IS the pick — its one free add-on goes on the order. -->
                        <div v-if="lifeFreeAddon" class="info-row">
                            <span class="label">Free with this plan</span>
                            <span class="value">
                                {{ lifeFreeAddon.name }} — FREE for life
                                <div class="free-addon-note">
                                    Ships every 3 months, free for as long as the GLP-1 plan
                                    is active. It ends if the GLP-1 plan is canceled.
                                </div>
                            </span>
                        </div>
                        <div v-if="activeOffer && !lifeFreeAddon && freeAddonOptions.length" class="info-row">
                            <span class="label">Free with this plan</span>
                            <span class="value">
                                <select v-model="selectedFreeAddonId">
                                    <option value="">None</option>
                                    <option v-for="addon in freeAddonOptions" :key="addon.id" :value="addon.id">
                                        {{ addon.name }} — {{ freeAddonMonths }} month{{ freeAddonMonths === 1 ? '' : 's' }} free
                                    </option>
                                </select>
                                <div v-if="selectedFreeAddonId" class="free-addon-note">
                                    Free months start a 1-month subscription that renews on its
                                    anniversary after the free period. Cancel anytime.
                                </div>
                            </span>
                        </div>

                        <div v-if="selectedProductName && selectedPlan" class="info-row">
                            <span class="label">Price:</span>
                            <span class="value">
                                ${{ (chargeTotal - paymentInfo.discount).toFixed(2) }}
                                <span v-if="addonTotal > 0" class="price-breakdown">
                                    (plan ${{ Number(currentPrice).toFixed(2) }} + add-ons ${{ addonTotal.toFixed(2) }})
                                </span>
                            </span>
                        </div>
                        <div class="info-row">
                            <span class="label">Shipping Address</span>
                            <span class="value">
                                <google-places-autocomplete :api-key="googleMapsApiKey" @address-updated="getShippingAddress" />
                            </span>
                        </div>
                        <div class="info-row">
                            <span class="label">Billing Address</span>
                            <span class="value">
                                <div>
                                    <label>
                                        <input type="checkbox" v-model="useSameAddress" @change="copyShippingToBilling">
                                        Same as shipping address
                                    </label>
                                </div>
                                <div v-if="!useSameAddress">
                                    <google-places-autocomplete :api-key="googleMapsApiKey" @address-updated="getBillingAddress" />
                                </div>
                            </span>
                        </div>
                    </div>
                    <div class="info-row">
                        <span class="label">Enter Card</span>
                        <span class="value">
                            <div id="card-element" class="ns-card-element" ref="cardElement"></div>
                        </span>
                    </div>
                    <div class="charge-group">

                        <!-- Paid add-ons. Rendered only once a main product AND plan are
                             chosen: these ride the main order's box, and a cart with no
                             main product creates subscriptions but no order at all. -->
                        <div v-if="addonOptions.length" class="info-row">
                            <span class="label">Add-ons</span>
                            <span class="value">
                                <div v-for="group in addonGroups" :key="group.name" class="addon-group">
                                <div class="addon-group-name">{{ group.name }}</div>
                                <div v-for="addon in group.items" :key="addon.id" class="addon-row">
                                    <label class="addon-pick">
                                        <input type="checkbox"
                                               :checked="addon.id in selectedAddons"
                                               @change="toggleAddon(addon)">
                                        {{ addon.name }}
                                    </label>
                                    <select v-if="addon.id in selectedAddons"
                                            :value="selectedAddons[addon.id]"
                                            @change="setAddonPlan(addon.id, $event.target.value)">
                                        <option v-for="plan in addon.plans" :key="plan.key" :value="plan.key">
                                            {{ plan.label }}
                                        </option>
                                    </select>
                                </div>
                                </div>
                                <div v-if="selectedAddonLines.length" class="addon-total">
                                    Add-ons: ${{ addonTotal.toFixed(2) }}
                                </div>
                            </span>
                        </div>

                        <!-- Replace the existing coupon section with this updated version -->
                        <!-- Replace your existing coupon input section with this -->
                        <div class="info-row">
                            <span class="label">Coupon</span>
                            <span class="value">
                                <div class="coupon-input-container">
                                    <select v-model="couponCode" @change="checkCoupon()" class="tf_textfield-3 w-input" style="width:50%; margin-right:10px" :class="{'invalid': couponError}" :disabled="couponApplied || !allowedCoupons.length">
                                        <option value="" disabled>{{ selectedPlan ? '-- Select coupon --' : 'Select a plan first' }}</option>
                                        <option v-for="code in allowedCoupons" :key="code" :value="code">{{ code }}</option>
                                    </select>
                                    <!-- X button to remove coupon -->
                                    <button v-if="couponApplied" @click="removeCoupon" class="remove-coupon-btn" title="Remove Coupon">
                                        <i class="material-icons">close</i>
                                    </button>
                                </div>
                                <div v-if="isMonthlyPlan" style="color:#b26a00; font-size:12px; margin-top:4px;">
                                    Coupons are not applicable on the Monthly plan.
                                </div>
                            </span>
                        </div>

                        <!-- Success message when coupon is applied -->
                        <div class="info-row" v-if="couponApplied && !couponError">
                            <span class="label" style="color:green;">
                                <i class="material-icons" style="font-size: 16px; vertical-align: middle;">check_circle</i>
                                Coupon Applied
                            </span>
                            <span class="value" style="color:green;">
                                {{ paymentInfo.couponCode }} - ${{ paymentInfo.discount }} off
                            </span>
                        </div>

                        <!-- Error message when coupon is invalid -->
                        <div class="info-row" v-if="couponError">
                            <span class="label" style="color:red;">
                                <i class="material-icons" style="font-size: 16px; vertical-align: middle;">error</i>
                                Invalid Coupon
                            </span>
                            <span class="value" style="color:red;">
                                {{ couponErrorMessage || 'This coupon is not set up for this plan' }}
                            </span>
                        </div>
                        <div class="info-row">
                            <button @click="getStripeToken" :disabled="!canProcessPayment || submitted" class="md-btn md-btn-primary">
                                <i class="material-icons">payment</i>
                                {{ submitted ? 'Processing...' : 'Process Payment' }}
                            </button>
                        </div>

                        <!-- Payment Error Display -->
                        <div v-if="paymentError" class="info-row">
                            <div class="error-message md-alert md-alert-error">
                                <i class="material-icons">error</i>
                                <p>{{ errorMessage }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Right Column: Pricing Configuration -->
        <div class="right-column">
            <div class="pricing-panel md-card">
                <h2 class="md-card-title">{{AppName}} - Pricing Configuration</h2>

                <p v-if="!plansLoaded" style="color:#b00020;">
                    Plans not loaded yet — if this persists, the plans API is unreachable and checkout is disabled.
                </p>

                <!-- One section per product the agent can actually charge, same list
                     and same promo-wins prices as the charge dropdown. Bundles show
                     what is in them so the agent can tell the customer what the plan
                     includes without opening anything else. -->
                <div v-for="product in productOptions" :key="product.id" class="pricing-section">
                    <h3 class="md-card-subtitle">{{ product.name }}</h3>
                    <p v-if="product.includes.length" class="pricing-includes">
                        Includes: {{ product.includes.join(' + ') }}
                    </p>
                    <table class="md-table">
                        <thead>
                            <tr>
                                <th>Plan</th>
                                <th>Price (USD)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="plan in (plansByProduct[product.id] || [])" :key="plan.key">
                                <td>{{ plan.termsLabel }}</td>
                                <td>${{ plan.price.toFixed(2) }} </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </div>

    <div v-else class="not-found">
        <p>User not found</p>
    </div>

    <!-- Update Cart Dialog -->
    <div v-if="showUpdateCartDialog" class="dialog-overlay">
        <div class="dialog-content md-dialog">
            <div class="md-dialog-header">
                <h3>Select Product</h3>
                <button @click="cancelUpdateCart" class="md-dialog-close">
                    <i class="material-icons">close</i>
                </button>
            </div>

            <div class="md-dialog-body">
                <div class="product-selection">
                    <div :class="['product-option md-card', { 'md-card-selected': selectedProduct === 'Tirzepatide' }]" @click="selectedProduct = 'Tirzepatide'">
                        <div class="product-icon">
                            <i class="material-icons">medication</i>
                        </div>
                        <p class="product-name">Tirzepatide</p>
                    </div>

                    <div :class="['product-option md-card', { 'md-card-selected': selectedProduct === 'Semaglutide' }]" @click="selectedProduct = 'Semaglutide'">
                        <div class="product-icon">
                            <i class="material-icons">medication</i>
                        </div>
                        <p class="product-name">Semaglutide</p>
                    </div>
                </div>
            </div>

            <!-- The offer rides on the cart link: the call-center checkout page reads it
                 from the cart and adds the NAD line once the customer picks a plan length
                 the offer covers. Validated by the backend when the cart is saved. -->
            <div v-if="selectedProduct && cartOffers.length" class="md-dialog-body cart-offer">
                <div class="info-row">
                    <span class="label">Offer</span>
                    <span class="value">
                        <select v-model="cartOfferSlug" @change="cartOfferChoice = ''">
                            <option value="">None</option>
                            <option v-for="offer in cartOffers" :key="offer.slug" :value="offer.slug">{{ offer.name }}</option>
                        </select>
                    </span>
                </div>
                <div v-if="cartOffer" class="info-row">
                    <span class="label">Free with this plan</span>
                    <span class="value">
                        <!-- Only an offer with several free add-ons (free-nad) needs a pick. -->
                        <select v-if="cartOfferChoices.length > 1" v-model="cartOfferChoice">
                            <option value="">Choose…</option>
                            <option v-for="c in cartOfferChoices" :key="c.value" :value="c.value">{{ c.label }}</option>
                        </select>
                        <template v-else-if="cartOfferChoices.length">{{ cartOfferChoices[0].label }}</template>
                        <div class="free-addon-note">
                            Applies when the customer picks a {{ cartOfferTermsLabel }}-month plan on the checkout page.
                        </div>
                    </span>
                </div>
            </div>

            <div class="md-dialog-footer">
                <button @click="cancelUpdateCart" class="md-btn md-btn-secondary">Cancel</button>
                <button @click="submitUpdateCart" class="md-btn md-btn-primary" :disabled="!selectedProduct || (cartOffer && !cartOfferPick)">
                    <i class="material-icons">check</i> Submit
                </button>
            </div>
        </div>
    </div>

</div>
</template>

<script>
import { loadStripe } from "@stripe/stripe-js";

const stripe = await loadStripe(process.env.VUE_APP_STRIPE_KEY);

import GooglePlacesAutocomplete from './GooglePlacesAutocomplete.vue'
import { couponsForTerms } from '../services/coupons'

// One id per checkout POST, sent as X-Correlation-Id. The backend derives the Stripe
// idempotency key from it, so it MUST be fresh for every attempt — a reused id on a
// retry collides with the first attempt's parameters and Stripe rejects the charge
// ("Keys for idempotent requests can only be used with the same parameters they were
// first used with") instead of running the card. crypto.randomUUID() is undefined
// outside a secure context, so fall back rather than throw before the POST goes out.
function newCorrelationId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
}

export default {
    name: 'UserDetails',
    components: {
        GooglePlacesAutocomplete
    },
    data() {
        return {
            user: null,
            loading: true,
            error: null,
            cart: null,
            app: process.env.VUE_APP_NAME,
            orders: null,

            // Whether a new signup would actually be accepted, and what refused it.
            // Both come from /users/:id, which computes them with the same guard the
            // checkout enforces — so this panel can never disagree with the POST.
            // null until the fetch lands: the card form stays hidden while unknown.
            chargeable: null,
            blockedBy: null,

            // FIXED: Added missing dialog properties that were referenced in template but not defined
            // Reasoning: Vue template was trying to access these properties for v-if directives but they weren't reactive
            showUpdateCartDialog: false,
            couponValid: false, // Tracks if the coupon is valid
            couponApplied: false,
            selectedProduct: null,
            // Offer for the cart link: slug, then the free add-on as 'free:<productId>'.
            cartOfferSlug: '',
            cartOfferChoice: '',
            phoneNumber: '',
            submittingCart: false,
            sendingLink: false,
            smsStatus: null, // 'success' or 'error'
            smsStatusMessage: '',
            processing: false,
            submitted: false,
            paymentError: false,
            errorMessage: '',
            paymentAttempts: 0,
            maxPaymentAttempts: 3,
            // Correlation id of the checkout POST currently in flight / last sent. Cleared
            // only when the server actually ANSWERED, so a retry after a timeout reuses it.
            checkoutCorrelationId: null,
            couponError: false,
            // Backend reason when it gives one (e.g. a limited coupon is used up).
            couponErrorMessage: '',
            trustUrl: '',
            errorReason: false,
            googleMapsApiKey: process.env.VUE_APP_GOOGLE_KEY,
            // FIXED: Added logo property that was referenced in template but missing from data
            // Reasoning: Template was trying to bind :src="logo" but logo wasn't defined as a reactive property
            logo: process.env.VUE_APP_LOGO,
            AppName: process.env.VUE_APP_NAME,
            cardElement: null,
            selectedProductId: '',
            selectedProductName: '',
            selectedPlan: '',
            // Live checkout offers from /api/checkout-offers (cached with the catalog).
            // The free-NAD giveaway is presented ONLY when a live offer says it runs on
            // the product and plan length the agent picked — never from a hardcoded slug,
            // because an offer that has ended or was never set up on this brand does not
            // error, it just bills the customer for the "free" line.
            offers: [],
            // Product id of the free add-on the agent chose, '' for none.
            selectedFreeAddonId: '',
            // The offer the agent picked for this sale ('' = none).
            selectedOfferSlug: '',

            // Paid add-ons the agent has attached, as { [productId]: planKey }.
            // Deliberately keyed by product so one product cannot be added twice at
            // two different terms.
            selectedAddons: {},

            // Products loaded from /get-all-products (localStorage-cached, alongside
            // plans). The charge dropdown is built from this rather than a hardcoded
            // pair of ObjectIds: the same codebase is deployed per brand, so what is
            // sellable differs between them, and a new product (e.g. a bundle) should
            // appear as soon as it exists in that brand's DB — not on a frontend
            // release. It also keeps `product_name` on the sale correct; see
            // processPayment.
            products: [],

            // Plans loaded from /get-all-plans (localStorage-cached). Canonical active
            // plans + hidden promo plans (isPromo:true) — the promo price is only
            // applied when its PricingPlans id is sent as planId at checkout.
            allPlans: [],
            promoPlans: [],
            // Dropdown value: the selected plan's id (or 'fallback-<terms>' when the
            // plans API/cache is unavailable and we fall back to env pricing).
            selectedPlanKey: '',
            currentPrice: 0,
            couponCode: '',
            useSameAddress: true,
            shippingAddress: {
                addressLine1: '',
                addressLine2: '',
                city: '',
                state: '',
                zip: ''
            },
            billingAddress: {
                addressLine1: '',
                addressLine2: '',
                city: '',
                state: '',
                zip: ''
            },
            paymentInfo: {
                payment_terms: 6,
                user_id: '',
                cardToken: '',
                amount: 0,
                discount: 0,
                medication: '',
                couponCode: '',
                drug_mode: '',
                couponId: null,
                paymentMethod: 'card',
                cartItems: [],
                cardDetails: null,
                planId: '',
                // Card info from Basis Theory
                cardBin: '',
                cardLast4: '',
                cardBrand: '',
            },


            // FIXED: Added plan labels initialization with proper error handling  
            // Reasoning: Plan labels are used in template for displaying pricing tables, need to be reactive
            planLabels: this.initializePlanLabels()
        }
    },

    computed: {
        // Why the charge panel is hidden. `blocked_by.type` is 'subscription' (they are
        // still on a plan) or 'order' (something is still being fulfilled) — an agent
        // needs to tell those apart, since only the second one clears on its own.
        blockedTitle() {
            if (this.blockedBy && this.blockedBy.type === 'subscription') {
                return 'Active Subscription';
            }
            return 'Order Already Placed';
        },

        blockedMessage() {
            if (this.blockedBy && this.blockedBy.type === 'subscription') {
                return 'This user already has an active subscription. Cancel it before signing them up on a new plan.';
            }
            if (this.blockedBy && this.blockedBy.type === 'order') {
                return 'This user has an order still being fulfilled. Resolve it before charging them again.';
            }
            return 'This user has completed their order. Cart management is disabled.';
        },

        // /create-lead forwards the dialer's agent id as ?agent=<id>; checkout
        // reports it back to the backend as vici_agent so the sale is attributed.
        viciAgent() {
            const agent = this.$route.query.agent;
            return agent ? String(agent) : null;
        },

        // Coupons are not allowed on the Monthly plan (payment_terms === 1)
        isMonthlyPlan() {
            return parseInt(this.selectedPlan, 10) === 1;
        },

        allowedCoupons() {
            return couponsForTerms(this.selectedPlan);
        },

        canProcessPayment() {
            return this.selectedProductId &&
                this.selectedPlan &&
                this.shippingAddress.addressLine1 &&
                this.billingAddress.addressLine1 &&
                this.cardElement &&
                !this.submitted &&
                this.currentPrice > 0;
        },

        // Plan dropdown options for the selected product — ONE option per terms so
        // agents never choose between two prices for the same plan: the promo plan
        // when one exists, the canonical plan otherwise. Empty until plans load
        // (from localStorage or the API) — no plans means no known prices, so
        // checkout stays blocked rather than guessing from a stale snapshot.
        // Main products this brand can actually be charged for: not an add-on, and
        // carrying at least one sellable plan. The plan test is what keeps the
        // one-time fee products (Priority Physician Review, Overnight Shipping) out
        // — they exist only on terms 0, which planOptions already refuses, so an
        // agent picking one would land on an empty plan list and a dead form.
        productOptions() {
            const sellable = new Set(
                [...this.allPlans, ...this.promoPlans]
                    .filter((plan) => ![0, 2].includes(Number(plan.terms)))
                    .map((plan) => String(plan.product))
            );

            // Bundles are named for the offer, not the contents, and several can share
            // one name — all four NewSelf "90 Day Total Reset Plan" rows do. Naming the
            // components is the only thing that tells them apart (Tirz vs Sema,
            // injectable vs nasal), and picking the wrong one is a mis-sale. Resolved
            // through the product list itself, so no slug-to-label map to maintain.
            const nameBySlug = {};
            for (const product of this.products) {
                if (product.internal_id) nameBySlug[String(product.internal_id)] = product.name;
            }
            const componentsOf = (product) => (product.bundle_components || [])
                .map((slug) => nameBySlug[String(slug)])
                .filter(Boolean);

            return this.products
                .filter((product) => product.is_addon !== true)
                .filter((product) => product.is_active !== false)
                .filter((product) => sellable.has(String(product.id)))
                .map((product) => {
                    const includes = componentsOf(product);
                    return {
                        id: product.id,
                        // The offer's own name, and what is in it, kept apart: the
                        // dropdown needs one unambiguous line, the pricing panel reads
                        // better as a heading plus an "Includes" line an agent can read
                        // to the customer.
                        name: product.name,
                        includes,
                        label: includes.length ? `${product.name} (${includes.join(' + ')})` : product.name,
                        // Lowercased slug, matching the 'semaglutide' / 'tirzepatide' values
                        // this component has always put in `medication` and the cart line.
                        slug: String(product.internal_id || product.name || '').toLowerCase()
                    };
                })
                .sort((a, b) => a.label.localeCompare(b.label));
        },

        // Sellable plans per product id, ONE per terms with the promo price winning.
        // Single source for both the plan dropdown (the selected product) and the
        // pricing panel (every product), so an agent can never be quoted one price
        // from the table and charged another from the dropdown.
        plansByProduct() {
            const byProduct = {};
            const add = (plan) => {
                // Terms 0 and 2 are not sellable plans — 0 is the one-time fee rail.
                if ([0, 2].includes(Number(plan.terms))) return;
                const productId = String(plan.product);
                if (!byProduct[productId]) byProduct[productId] = {};
                byProduct[productId][plan.terms] = plan;
            };
            this.allPlans.forEach(add);
            this.promoPlans.forEach(add); // promo wins

            const out = {};
            for (const productId of Object.keys(byProduct)) {
                out[productId] = Object.values(byProduct[productId])
                    .map((plan) => ({
                        key: plan.id,
                        planId: plan.id,
                        terms: plan.terms,
                        price: Number(plan.price),
                        isPromo: !!plan.isPromo,
                        termsLabel: this.planLabels[String(plan.terms)] || `${plan.terms} mo`,
                        label: `${this.planLabels[String(plan.terms)] || plan.terms + ' mo'} — $${Number(plan.price).toFixed(2)}`
                    }))
                    .sort((a, b) => a.terms - b.terms);
            }
            return out;
        },

        planOptions() {
            if (!this.selectedProductId) return [];
            return this.plansByProduct[String(this.selectedProductId)] || [];
        },

        // The live offer covering the current product + plan length, or null.
        // `free_addon_terms` is keyed by main plan length ("3"/"6"/"12"); a length with
        // no key earns nothing, which is how an offer runs on some plans and not others.
        // The main product the Update Cart dialog picked ('Tirzepatide' / 'Semaglutide').
        cartProductId() {
            if (!this.selectedProduct) return '';
            const want = String(this.selectedProduct).toLowerCase();
            const product = this.products.find((p) => p.is_addon !== true && String(p.name).toLowerCase() === want);
            return product ? String(product.id) : '';
        },

        // Offers that run on that product. No plan yet — the customer picks it on the
        // checkout page, which shows the offer only on its eligible plan lengths.
        cartOffers() {
            if (!this.cartProductId) return [];
            return this.offers.filter((offer) => {
                const qualifying = offer.qualifying_product_ids || [];
                return !qualifying.length || qualifying.includes(this.cartProductId);
            });
        },

        cartOffer() {
            return this.cartOffers.find((o) => o.slug === this.cartOfferSlug) || null;
        },

        cartOfferTermsLabel() {
            const map = (this.cartOffer && this.cartOffer.free_addon_terms) || {};
            return Object.keys(map).filter((k) => Number(map[k]) > 0).sort((a, b) => a - b).join('/');
        },

        cartOfferChoices() {
            const offer = this.cartOffer;
            if (!offer) return [];
            const life = offer.free_addon_for_life === true;
            const allowed = offer.free_addon_slugs || [];
            return this.products
                .filter((p) => allowed.includes(String(p.internal_id)) && p.is_active !== false)
                .map((p) => ({
                    value: 'free:' + p.id,
                    label: life ? `${p.name} — FREE for life (ships every 3 months)` : `${p.name} — free months`,
                }));
        },

        // The free add-on the cart link carries: the agent's pick, or the only one.
        cartOfferPick() {
            if (this.cartOfferChoice) return this.cartOfferChoice;
            return this.cartOfferChoices.length === 1 ? this.cartOfferChoices[0].value : '';
        },

        qualifyingOffers() {
            const terms = parseInt(this.selectedPlan, 10);
            if (!this.selectedProductId || !terms) return [];

            return this.offers.filter((offer) => {
                const qualifying = offer.qualifying_product_ids || [];
                // An empty list means the offer runs on any main product — deliberate
                // when the offer was created, so it is honoured here.
                if (qualifying.length && !qualifying.includes(this.selectedProductId)) return false;
                const map = offer.free_addon_terms || {};
                return Number(map[String(terms)]) > 0;
            });
        },

        // The offer the agent picked, while it still covers the product + plan.
        activeOffer() {
            return this.qualifyingOffers.find((o) => o.slug === this.selectedOfferSlug) || null;
        },

        // free-nad-nasal-life / free-nad-injection-life: the free add-on ships every
        // 3 months for as long as the GLP-1 plan is active.
        isLifeOffer() {
            return !!(this.activeOffer && this.activeOffer.free_addon_for_life === true);
        },

        // A for-life offer gives away exactly one add-on, so picking the offer picks it.
        lifeFreeAddon() {
            return this.isLifeOffer && this.freeAddonOptions.length === 1 ? this.freeAddonOptions[0] : null;
        },

        // The free add-on going on this order.
        chosenFreeAddonId() {
            if (!this.activeOffer) return '';
            return this.lifeFreeAddon ? this.lifeFreeAddon.id : this.selectedFreeAddonId;
        },

        // The add-on products this offer may give away, resolved through the loaded
        // catalog so the ids and names come from the same place everything else does.
        freeAddonOptions() {
            const offer = this.activeOffer;
            if (!offer) return [];
            const allowed = offer.free_addon_slugs || [];
            return this.products
                .filter((product) => allowed.includes(String(product.internal_id)))
                .filter((product) => product.is_active !== false)
                .map((product) => ({ id: product.id, name: product.name }));
        },

        // How many months the giveaway is worth at the selected plan length — the offer
        // states it, so it is read rather than restated.
        freeAddonMonths() {
            const offer = this.activeOffer;
            if (!offer) return 0;
            return Number((offer.free_addon_terms || {})[String(parseInt(this.selectedPlan, 10))]) || 0;
        },

        // Paid add-on products the agent may attach to this sale.
        //
        // These are `is_addon: true`, so productOptions (main products) excludes them
        // by design — without this they cannot be sold by an agent at all, which is
        // the gap this fills.
        //
        // Offered ONLY once a main product and plan are chosen. A cart with no main
        // product creates subscriptions and NO order (the add-on loop in
        // createCheckout never calls order.create), so the customer would be charged
        // and nothing would ship. Requiring the GLP first is what makes these
        // shippable: the add-on lines ride the main order's box.
        addonOptions() {
            if (!this.selectedProductId || !this.selectedPlan) return [];

            // Variant groups are ONE entry in the feed carrying a `variants` array, and
            // the group's top-level id is just its first variant's. Offering the group
            // as a single row would silently sell the lowest strength — Estradiol Oral
            // would always be the 0.5mg. Expanded into one row per real product id,
            // labelled with the strength.
            const rows = [];
            for (const product of this.products) {
                if (product.is_addon !== true) continue;
                if (product.is_active === false) continue;
                // `shop_details.is_active` is the switch that decides what is actually
                // sellable — Products.is_active is an ops flag and is true even for
                // items with no dispensing path (Testosterone, Minoxidil, GHK-Cu and
                // the rest are is_active:true but deliberately switched off).
                if (!product.shop_details || product.shop_details.is_active !== true) continue;
                // The free-NAD giveaway is chosen separately; offering the same product
                // here too would let an agent add it twice, once at $0 and once at full
                // price.
                if (this.freeAddonOptions.some((a) => a.id === product.id)) continue;

                const variants = Array.isArray(product.variants) && product.variants.length
                    ? product.variants
                    : [{ id: product.id, variant: null }];
                for (const v of variants) {
                    rows.push({
                        id: v.id,
                        name: v.variant ? `${product.name} — ${v.variant}` : product.name,
                        category: (product.category && product.category.primary) || 'Other',
                        plans: this.plansByProduct[String(v.id)] || [],
                    });
                }
            }

            // Anything still sharing a label is a product that SHOULD be in a variant
            // group and is not (Finasteride 1mg/5mg today). Two identical rows is how
            // an agent sells the wrong strength, so disambiguate rather than hide it.
            const seen = {};
            for (const r of rows) seen[r.name] = (seen[r.name] || 0) + 1;
            for (const r of rows) if (seen[r.name] > 1) r.name = `${r.name} (${r.id.slice(-6)})`;

            return rows
                .filter((r) => r.plans.length)
                .sort((a, b) => a.name.localeCompare(b.name));
        },

        // The same add-ons grouped by catalog category, which is how the agent cheat
        // sheet is organised — agents look for "Sexual Health" or "Longevity", not a
        // flat alphabetical list of 60-odd products. Category comes from the product
        // row rather than the sheet's marketing names so it cannot drift from what is
        // actually in the catalog.
        addonGroups() {
            const byCategory = {};
            for (const row of this.addonOptions) {
                const key = row.category || 'Other';
                (byCategory[key] ??= []).push(row);
            }
            return Object.keys(byCategory)
                .sort((a, b) => (a === 'Other' ? 1 : b === 'Other' ? -1 : a.localeCompare(b)))
                .map((name) => ({ name, items: byCategory[name] }));
        },

        // The attached add-ons resolved to their chosen plan, for the payload and the
        // running total.
        selectedAddonLines() {
            const out = [];
            for (const [productId, planKey] of Object.entries(this.selectedAddons)) {
                const product = this.addonOptions.find((a) => a.id === productId);
                if (!product) continue;
                const plan = product.plans.find((p) => p.key === planKey) || product.plans[0];
                if (plan) out.push({ id: productId, name: product.name, plan });
            }
            return out;
        },

        addonTotal() {
            return this.selectedAddonLines.reduce((sum, l) => sum + Number(l.plan.price || 0), 0);
        },

        // What the card is actually charged: the plan plus every attached add-on.
        // `amount` is what BNPL approves and what the CallConversion row records, so
        // it has to be the real total, not just the main plan.
        chargeTotal() {
            return Number((Number(this.currentPrice || 0) + this.addonTotal).toFixed(2));
        },

        plansLoaded() {
            return this.allPlans.length > 0;
        },
    },

    async created() {
        // Get the user ID from the URL parameter
        const userId = this.$route.params.id;

        // Fetch user details when component is created
        this.fetchUserDetails(userId);

        // Plans come from localStorage when fresh — no API call on every open
        this.loadPlans();
    },

    mounted() {
        // Load Material Icons if not already loaded
        if (!document.querySelector('link[href*="material-icons"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://fonts.googleapis.com/icon?family=Material+Icons';
            document.head.appendChild(link);
        }
    },

    methods: {
        // FIXED: Added helper method to safely initialize plan labels
        // Reasoning: Plan labels need to be consistent and available for the pricing tables
        initializePlanLabels() {
            return {
                "1": "Monthly",
                "3": "Quarterly",
                "6": "Semi Annual",
                "12": "Annual"
            };
        },

        // No notification plugin is registered ($notify doesn't exist), so a
        // direct this.$notify() call throws — inside updatePlanAndPrice that
        // aborted the handler and left selectedPlan/currentPrice/planId stale
        // after a plan change, sending old payment_terms to /validate-coupon.
        // Restored after the PR #3 merge dropped it. Keep all notification
        // calls going through this helper — lint enforces it.
        notify(payload) {
            /* eslint-disable no-restricted-syntax -- sole sanctioned $notify access */
            if (typeof this.$notify === 'function') {
                this.$notify(payload);
            } else {
                console.log(`[${payload.type}] ${payload.text}`);
            }
            /* eslint-enable no-restricted-syntax */
        },

        async handleCouponSubmit() {
            // Reasoning: Centralized coupon handling with proper validation
            this.errorMessage = null;
            if (this.isMonthlyPlan) {
                this.notify({
                    type: 'warning',
                    text: 'Coupons are not applicable on the Monthly plan.'
                });
                return;
            }
            if (!this.couponCode) {
                this.errorMessage = 'Please enter a coupon code';
                return;
            }
            // A coupon is validated against the selected plan's terms — with no
            // plan picked the request would go out with payment_terms: null.
            if (!this.selectedPlan) {
                this.paymentError = true;
                this.errorMessage = 'Please select a plan before applying a coupon';
                return;
            }
            this.paymentError = false;
            this.paymentInfo.couponCode = this.couponCode;
            await this.validateCoupon();
        },

        async validateCoupon() {
            // Reasoning: Server-side coupon validation ensures accurate discount application
            if (this.couponApplied || this.isMonthlyPlan) return;
            this.couponError = false;
            this.couponErrorMessage = '';
            try {
                // Use the existing $http instance instead of $axios for consistency
                const response = await this.$http.post('/validate-coupon', {
                    coupon: this.paymentInfo.couponCode,
                    payment_terms: parseInt(this.selectedPlan)
                });

                if (response.data.status === 'success') {
                    this.couponValid = true;
                    this.couponApplied = true;
                    this.paymentInfo.couponId = response.data.coupon_id;
                    this.paymentInfo.discount = response.data.c_val;
                } else {
                    this.couponValid = false;
                    this.couponError = true;
                    this.couponErrorMessage = response.data.message || '';
                }
            } catch (error) {
                this.couponValid = false;
                this.couponError = true;
            }
        },

        checkCoupon() {
            // Reasoning: Wrapper method to maintain compatibility with existing template calls
            this.handleCouponSubmit();
        },

        async createCardElement() {
            if (stripe && this.$refs.cardElement) {
                try {
                    const elements = stripe.elements();
                    this.cardElement = elements.create('card');
                    this.cardElement.mount('#card-element');
                    console.log('Card element mounted successfully');

                    this.cardElement.on("change", (event) => {
                        if (event.brand) {
                            this.paymentInfo.cardBrand = event.brand;
                        }
                    });
                } catch (error) {
                    console.error('Error mounting card element:', error);
                }
            }
        },

        toggleAddon(addon) {
            if (addon.id in this.selectedAddons) {
                this.$delete(this.selectedAddons, addon.id);
                return;
            }
            // Default to the shortest term the product offers — the cheapest thing to
            // agree to on a call, and the agent can change it right there.
            this.$set(this.selectedAddons, addon.id, addon.plans[0].key);
        },

        setAddonPlan(productId, planKey) {
            this.$set(this.selectedAddons, productId, planKey);
        },

        updateSelectedProduct() {
            this.resetCouponOnSelectionChange(); // ADD THIS LINE

            // Resolved from the loaded product, not a hardcoded id pair — a product
            // the list offers must always produce a name, or the sale is recorded
            // against a blank one (see processPayment).
            const selected = this.productOptions.find((p) => p.id === this.selectedProductId);
            this.selectedProductName = selected ? selected.slug : '';
            // Plan options are per-product (plan ids), so a product switch invalidates the pick
            this.selectedPlanKey = '';
            // ...and so does the giveaway: offers qualify on product AND plan length, so a
            // stale pick could send a free line the new selection never earned.
            this.selectedFreeAddonId = '';
            this.selectedOfferSlug = '';
            // Paid add-ons go too. They are only offered once a main product and plan
            // are chosen, so a product switch leaves them attached to a selection that
            // no longer exists.
            this.selectedAddons = {};
            this.updatePlanAndPrice();
        },

        updatePlanAndPrice() {
            if (this.selectedPlanKey) {
                this.resetCouponOnSelectionChange(); // ADD THIS BLOCK
            }

            const opt = this.planOptions.find(o => o.key === this.selectedPlanKey);
            if (opt) {
                // selectedPlan stays the terms string — coupon validation and
                // payment_terms are keyed off it.
                this.selectedPlan = String(opt.terms);
                this.currentPrice = opt.price;
                this.paymentInfo.planId = opt.planId;
            } else {
                this.selectedPlan = '';
                this.currentPrice = 0;
                this.paymentInfo.planId = '';
            }

            // The giveaway qualifies on plan LENGTH, so a plan change can take it away
            // (monthly earns nothing) or change which add-ons it covers. Drop a pick that
            // the new selection no longer offers rather than sending a line the customer
            // has not earned — the backend would refuse the grant and bill for it.
            if (this.selectedOfferSlug && !this.activeOffer) {
                this.selectedOfferSlug = '';
            }
            if (this.selectedFreeAddonId &&
                !this.freeAddonOptions.some((a) => a.id === this.selectedFreeAddonId)) {
                this.selectedFreeAddonId = '';
            }
        },

        // A different offer means a different giveaway: clear the pick.
        onOfferChange() {
            this.selectedFreeAddonId = '';
        },

        // Load plans from localStorage when fresh; otherwise hit /get-all-plans once
        // (canonical + promo) and cache the result. On API failure a stale cache is
        // still used; with neither, the panel shows no prices and checkout stays
        // blocked rather than quoting a number nothing confirmed.
        async loadPlans() {
            // v4: offers now carry free_addon_for_life; a v3 cache would show a for-life
            // offer as a time-boxed one for up to an hour.
            const CACHE_KEY = 'cc_plans_cache_v4';
            // Offers are cached with the catalog but turn on and off on a campaign
            // schedule, so the window is shorter than the catalog alone would need.
            const TTL_MS = 60 * 60 * 1000; // 1 hour

            let cached = null;
            try {
                cached = JSON.parse(localStorage.getItem(CACHE_KEY));
            } catch (e) {
                cached = null;
            }

            const useCached = (c) => {
                this.allPlans = c.plans;
                this.promoPlans = c.promoPlans || [];
                this.products = c.products || [];
                this.offers = c.offers || [];
            };

            if (cached && Array.isArray(cached.plans) && (Date.now() - cached.ts) < TTL_MS) {
                useCached(cached);
                return;
            }

            try {
                const [plansRes, promoRes, productsRes, offersRes] = await Promise.all([
                    this.$http.get('/get-all-plans'),
                    this.$http.get('/get-all-plans?isPromo=true'),
                    this.$http.get('/get-all-products'),
                    this.$http.get('/checkout-offers')
                ]);
                this.allPlans = (plansRes.data && plansRes.data.plans) || [];
                this.promoPlans = (promoRes.data && promoRes.data.plans) || [];
                this.products = (productsRes.data && (productsRes.data.products || productsRes.data.data)) || [];
                this.offers = (offersRes.data && offersRes.data.offers) || [];
                localStorage.setItem(CACHE_KEY, JSON.stringify({
                    ts: Date.now(),
                    plans: this.allPlans,
                    promoPlans: this.promoPlans,
                    products: this.products,
                    offers: this.offers
                }));
            } catch (error) {
                console.error('Error fetching plans/products:', error);
                if (cached && Array.isArray(cached.plans)) {
                    // Stale cache beats showing nothing — it at least came from the API
                    useCached(cached);
                }
            }
        },

        async fetchUserDetails(userId) {
            // Reasoning: Set loading state to provide user feedback during API calls
            this.loading = true;
            this.error = null;

            try {
                const response = await this.$http.get(`/users/${userId}`);
                this.user = response.data.user;
                this.cart = response.data.cart;
                this.orders = response.data.orders;

                // Fall back to the old orders-array rule when the backend has not yet
                // shipped `chargeable`. Without this, an older API would leave the flag
                // undefined and hide the charge panel from every agent for every user.
                this.chargeable = typeof response.data.chargeable === 'boolean'
                    ? response.data.chargeable
                    : !(response.data.orders && response.data.orders.length > 0);
                this.blockedBy = response.data.blocked_by || null;

                // Initialize phone number if available
                // Reasoning: Pre-populate phone field to improve user experience
                if (this.user && this.user.phone) {
                    this.phoneNumber = this.user.phone;
                }

                // Show refresh notification if this is not the initial load
                // Reasoning: Provide feedback when user manually refreshes data
                if (!this.loading) {
                    this.notify({
                        type: 'info',
                        text: 'User data refreshed successfully'
                    });
                }

                // Create card element after DOM is updated
                // Reasoning: Ensure DOM is ready before mounting payment card element
                await this.$nextTick();
                setTimeout(() => {
                    this.createCardElement();
                }, 100);
            } catch (err) {
                console.error('Error fetching user details:', err);
                this.error = 'Failed to load user details. Please try again later.';
            } finally {
                this.loading = false;
            }
        },

        isPOBox(address) {
            // Reasoning: Validate shipping addresses to prevent delivery issues
            // PO Boxes cannot receive certain types of shipments
            const normalized = address.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ');
            const poBoxRegex = /\b(po box|post office box|p o box)\b(?:\s*\d{0,6})?/i;
            return poBoxRegex.test(normalized);
        },

        getShippingAddress(address) {
            // Reasoning: Validate shipping address to prevent delivery failures
            if (this.isPOBox(address.street)) {
                this.notify({
                    type: 'error',
                    text: 'PO Box addresses are not allowed for shipping'
                });
                return;
            }

            // Reasoning: Structure address data consistently for backend processing
            this.shippingAddress = {
                addressLine1: address.street,
                addressLine2: address.address_line2 || '',
                city: address.city,
                state: address.state,
                zip: address.zip
            };

            // Reasoning: Auto-copy to billing if user selected same address option
            if (this.useSameAddress) {
                this.copyShippingToBilling();
            }
        },

        getBillingAddress(address) {
            // Reasoning: Structure billing address data for payment processing
            this.billingAddress = {
                addressLine1: address.street,
                addressLine2: address.address_line2 || '',
                city: address.city,
                state: address.state,
                zip: address.zip
            };
        },

        copyShippingToBilling() {
            // Reasoning: Reduce user input when billing and shipping addresses are the same
            if (this.useSameAddress) {
                this.billingAddress = {
                    ...this.shippingAddress
                };
            }
        },

        getStripeToken() {
            // Re-entry guard: the button's :disabled only updates on the next render,
            // so a fast double-click fires this handler twice. Bail synchronously if a
            // submit is already in flight — otherwise both clicks tokenize and POST
            // /call-center/checkout-stripe, double-charging the customer.
            if (this.submitted) return false;

            // Reasoning: Prevent multiple payment submissions and validate required fields
            this.submitted = true;
            this.paymentError = false;

            if (!this.validateAddress(this.shippingAddress)) {
                this.paymentError = true;
                this.errorMessage = "Please enter a complete shipping address.";
                this.submitted = false;
                return false;
            }

            if (!this.useSameAddress && !this.validateAddress(this.billingAddress)) {
                this.paymentError = true;
                this.errorMessage = "Please enter a complete billing address.";
                this.submitted = false;
                return false;
            }

            this.errorReason = false;
            // Tokenize card details via Stripe
            stripe.createToken(this.cardElement)
                .then((result) => {
                    if (result.error) {
                        this.paymentError = true;
                        this.errorMessage = result.error.message || "Invalid card details";
                        this.submitted = false;
                        return;
                    }
                    const token = result.token;
                    this.paymentInfo.cardToken = token.id;
                    this.paymentInfo.cardDetails = {
                        number: 'XXXXXXXXXXX' + token.card.last4,
                        expiration_month: token.card.exp_month,
                        expiration_year: token.card.exp_year,
                        cardBrand: token.card.brand,
                        cardBin: token.card.brand,
                        cardLast4: token.card.last4,
                    };
                    this.paymentInfo.cardBin = token.card.brand;
                    this.paymentInfo.cardLast4 = token.card.last4;
                    this.paymentInfo.paymentMethod = 'card';
                    this.processPayment();
                })
                .catch((error) => {
                    console.log(error);
                    this.paymentError = true;
                    this.errorMessage = "Invalid card details";
                    this.submitted = false;
                });
        },

        validateAddress(address) {
            // Reasoning: Ensure all required address fields are present for successful delivery/billing
            return address &&
                address.addressLine1 &&
                address.city &&
                address.state &&
                address.zip;
        },
        async blockCart(userId){
            try{
                return await this.$http.post('/call-center/block-card', {userId : userId});
            }catch(error){
                console.log(error)
            }
        },
        async processPayment() {
            try {
                
                // Prepare payment info
                // Reasoning: Structure payment data according to backend API expectations
                this.paymentInfo.user_id = this.user.id;
                this.paymentInfo.id = this.user.id;
                // planId = the selected PricingPlans id (promo or canonical), NOT the
                // product id — the backend matches it against {product, terms} and
                // silently charges the canonical price when it doesn't resolve.
                const selectedOpt = this.planOptions.find(o => o.key === this.selectedPlanKey);
                this.paymentInfo.planId = (selectedOpt && selectedOpt.planId) || '';
                this.paymentInfo.medication = this.selectedProductName;
                this.paymentInfo.payment_terms = parseInt(this.selectedPlan);
                if (this.paymentInfo.payment_terms === 1) {
                    // No coupons on the Monthly plan — strip any typed/stale coupon
                    this.paymentInfo.couponCode = '';
                    this.paymentInfo.couponId = null;
                    this.paymentInfo.discount = 0;
                } else {
                    this.paymentInfo.couponCode = this.couponCode;
                }
                this.paymentInfo.cartItems = [{
                    id: this.selectedProductId,
                    quantity: 1,
                    price: this.currentPrice,
                    name: this.selectedProductName,
                    retail_price: this.currentPrice
                }];

                // The free add-on line. Three things about it are load-bearing — see
                // newself-newforms src/checkout/lib/free-nad.js, which states them for the
                // lander that sells the same offer:
                //
                //  1. `terms: 1` is REQUIRED. Without it the backend's resolveAddonPlan
                //     falls back to a 3-month plan and the customer is enrolled at the
                //     3-MONTH price once the free window ends. (The agent checkout only
                //     started forwarding a line's terms in newself-backend #425 — before
                //     that this was impossible to get right from here.)
                //  2. It carries NO planId. There is none to name; the backend grants the
                //     free window from the offer slug plus the main plan's length.
                //  3. price and retail_price are 0, not the list price. They are display
                //     values the backend ignores, but `amount` below is what BNPL
                //     approves, and retail_price is what a summary strikes through — a
                //     real price in either invents money the customer never agreed to.
                //
                // The line still has to BE there: it is what creates the subscription and
                // gets the NAD prescribed. An order without it charges correctly and ships
                // only the GLP-1.
                // Paid add-ons. Each carries its OWN planId and terms: createCheckout
                // creates a subscription per line and resolves the plan from them, and
                // the call-center path only started forwarding per-line terms in
                // newself-backend #425 — without that they would all fall back to the
                // add-on default of 3 months.
                for (const line of this.selectedAddonLines) {
                    this.paymentInfo.cartItems.push({
                        id: line.id,
                        quantity: 1,
                        price: line.plan.price,
                        retail_price: line.plan.price,
                        name: line.name,
                        planId: line.plan.planId,
                        terms: line.plan.terms,
                        is_addon: true,
                        main_type: 0
                    });
                }

                const freeAddon = this.freeAddonOptions.find((a) => a.id === this.chosenFreeAddonId);
                if (freeAddon) {
                    this.paymentInfo.cartItems.push({
                        id: freeAddon.id,
                        quantity: 1,
                        price: 0,
                        retail_price: 0,
                        name: freeAddon.name,
                        // free-nad: 1 (see above). free-for-life: 3 — one free box is three
                        // months, the add-on plan length the backend bills it at ($0 via
                        // the forever coupon).
                        terms: this.isLifeOffer ? 3 : 1,
                        is_addon: true,
                        main_type: 0
                    });
                }

                // `product_name` is not cosmetic: the backend writes it straight onto the
                // CallConversion row, which is what the agent dashboard reports on. This
                // used to be an if/else over two product ids, so any third product would
                // have recorded the sale under a BLANK product name. Taken from the
                // loaded product instead.
                const selectedProduct = this.productOptions.find((p) => p.id === this.selectedProductId);
                const productDisplayName = selectedProduct ? selectedProduct.label : '';

                // Reasoning: Consolidate all payment data into single object for API call
                let paymentData = {
                    ...this.paymentInfo,
                    // The campaign the backend should apply, '' for an ordinary order. It
                    // is resolved against the CheckoutOffer table server-side, so an
                    // unknown or expired slug grants nothing and the sale proceeds as a
                    // normal one — the slug alone can never create a giveaway.
                    offer: freeAddon && this.activeOffer ? this.activeOffer.slug : '',
                    // Plan + paid add-ons. The free line adds nothing by definition.
                    // This is the figure BNPL approves and the one written onto the
                    // CallConversion row, so it has to be what is actually collected —
                    // sending the plan price alone would under-approve a BNPL order and
                    // under-report every agent's sale.
                    amount: this.chargeTotal,
                    cartTotal: this.chargeTotal,
                    cartTotalNoDiscount: this.chargeTotal,
                    billingAddress: this.billingAddress,
                    shippingAddress: this.shippingAddress,
                    discount: 0,
                    skipAuth: false,
                    user_name: `${this.user.first_name} ${this.user.last_name}`,
                    product_name: productDisplayName,
                    phone: `${this.user.phone}`,
                };

                if (this.viciAgent) {
                    paymentData.vici_agent = this.viciAgent;
                }

                const response = await this.sendRequest(paymentData);
                console.log(response)

                // Reasoning: Handle successful payment by refreshing data and resetting form
                if (response.data.status === 'success') {
                    // Keep submitted=true while the page reloads — resetting it here
                    // re-enables the pay button for a beat during unload.
                    window.location.reload();
                } else {
                    this.paymentError = true;
                    this.errorMessage = response.data.message;
                    this.submitted = false;
                    // Server answered and declined it: the next click is a genuinely new
                    // attempt and must not collide with this one's idempotency key.
                    this.checkoutCorrelationId = null;
                }
            } catch (error) {
                // Reasoning: Track payment attempts and provide user feedback on errors
                this.paymentAttempts++;
                this.paymentError = true;
                this.errorMessage = error.response?.data?.message || error.message || "";
                this.submitted = false;
                // error.response present = the server answered (decline/validation), so the
                // attempt is definitively over. Absent = timeout or network abort with the
                // charge possibly still in flight; KEEP the id so a retry can't double-charge.
                if (error.response) {
                    this.checkoutCorrelationId = null;
                }
                if (this.paymentAttempts >= 3) {
                    await this.blockCart(this.user.id);
                }
            }
        },
        // Add these two new methods
        removeCoupon() {
            this.couponCode = '';
            this.couponApplied = false;
            this.couponValid = false;
            this.couponError = false;
            this.paymentInfo.couponCode = '';
            this.paymentInfo.couponId = null;
            this.paymentInfo.discount = 0;
            
            this.notify({
                type: 'info',
                text: 'Coupon removed successfully'
            });
        },

        resetCouponOnSelectionChange() {
            if (this.couponApplied || this.couponCode) {
                this.couponCode = '';
                this.couponApplied = false;
                this.couponValid = false;
                this.couponError = false;
                this.paymentInfo.couponCode = '';
                this.paymentInfo.couponId = null;
                this.paymentInfo.discount = 0;
                
                this.notify({
                    type: 'warning',
                    text: 'Coupon reset due to medication/plan change. Please re-apply if needed.'
                });
            }
        },
        async sendRequest(paymentData) {
            // Send to your AdonisJS checkout endpoint
            // Reasoning: Centralize API call logic for easier maintenance and error handling
            // One id per charge ATTEMPT, not per POST: processPayment clears it only when the
            // server answered. So a retry after a timeout/network abort — where the first
            // charge may still be running — reuses the id, and the backend's Stripe
            // idempotency key stays the same instead of minting a second subscription.
            if (!this.checkoutCorrelationId) {
                this.checkoutCorrelationId = newCorrelationId();
            }
            return await this.$http.post('/call-center/checkout-stripe', paymentData, {
                headers: { 'X-Correlation-Id': this.checkoutCorrelationId },
                // The shared client aborts at 10s, which is short for a call that creates a
                // Stripe customer, attaches the card and creates a charging subscription.
                // An abort leaves the charge running server-side while the agent is told it
                // failed — the exact state that produces a duplicate submit.
                timeout: 60000,
            });
        },

        async saveTrustFrom(trustUrl) {
            // Implement trust form saving if needed
            // Reasoning: Placeholder for future trust form functionality
            try {
                // Your trust form saving logic here
                console.log('Saving trust form:', trustUrl);
            } catch (error) {
                console.error('Error saving trust form:', error);
            }
        },

        async updateAddress() {
            // Update user address if needed
            // Reasoning: Placeholder for future address update functionality
            try {
                // Your address update logic here
                console.log('Updating address');
            } catch (error) {
                console.error('Error updating address:', error);
            }
        },

        async clearCart() {
            // Clear cart after successful payment
            // Reasoning: Clean up cart data after order completion
            try {
                if (this.cart && this.cart.id) {
                    await this.$http.post('/clear-cart', {
                        cartId: this.cart.id
                    });
                }
            } catch (error) {
                console.error('Error clearing cart:', error);
            }
        },

        pushToRoute(amountCharged) {
            // Navigate to success page
            // Reasoning: Provide user feedback and update UI after successful payment
            this.notify({
                type: 'success',
                text: `Payment processed successfully! Amount charged: ${amountCharged}`
            });

            // Refresh user details to show updated order status
            this.fetchUserDetails(this.user.id);

            // You might want to navigate to a success page
            // this.$router.push('/payment-success');
        },

        goBack() {
            // Reasoning: Provide navigation option to return to previous page
            this.$router.go(-1);
        },

        editUser() {
            // Reasoning: Navigate to user edit page when user data needs modification
            if (this.user && this.user.id) {
                this.$router.push(`/users/${this.user.id}/edit`);
            }
        },

        // Cart Update Methods
        cancelUpdateCart() {
            // Reasoning: Reset dialog state when user cancels cart update
            this.showUpdateCartDialog = false;
            this.selectedProduct = null;
            this.cartOfferSlug = '';
            this.cartOfferChoice = '';
        },

        async submitUpdateCart() {
            // Reasoning: Validate selection before making API call
            if (!this.selectedProduct) return;

            this.submittingCart = true;

            try {
                // If cart doesn't exist, use "no_product_found" as cartId
                // Reasoning: Handle cases where user doesn't have an existing cart
                const cartId = this.cart && this.cart.id ? this.cart.id : "no_product_found";

                const choice = this.cartOffer ? this.cartOfferPick : '';
                await this.$http.post('/update-cart', {
                    cartId: cartId,
                    productName: this.selectedProduct,
                    userId: this.user.id, // Send userId to help backend create cart if needed
                    // The backend validates this against the live offer and stores it on
                    // the cart; omitted = the cart carries no offer.
                    offer: choice ? {
                        slug: this.cartOffer.slug,
                        choice: 'free',
                        addonProductId: choice.slice(5),
                    } : undefined,
                });

                // Refresh user details to show updated cart
                // Reasoning: Ensure UI reflects the updated cart data
                await this.fetchUserDetails(this.user.id);

                // Show success notification
                this.notify({
                    type: 'success',
                    text: `Cart updated successfully to ${this.selectedProduct}`
                });
            } catch (error) {
                console.error('Error updating cart:', error);
                this.notify({
                    type: 'error',
                    text: 'Failed to update cart. Please try again.'
                });
            } finally {
                // Reasoning: Reset form state regardless of success or failure
                this.submittingCart = false;
                this.showUpdateCartDialog = false;
                this.selectedProduct = null;
                this.cartOfferSlug = '';
                this.cartOfferChoice = '';
            }
        },

        // Send Cart Link SMS directly
        async sendCartLinkSMS() {
            // Reasoning: Validate required fields before making API call
            if (!this.user || !this.user.id || !this.phoneNumber) {
                this.smsStatus = 'error';
                this.smsStatusMessage = 'Phone number is required to send SMS';
                return;
            }

            // Reset status and start sending
            this.smsStatus = null;
            this.smsStatusMessage = '';
            this.sendingLink = true;

            try {
                // The link the backend builds is `/weightlosspayment/<cartId>`, so the
                // old "no_product_found" fallback here would have texted the customer a
                // dead page. Unreachable while the button was hidden without a cart;
                // now that it is always rendered, this is the guard behind the
                // disabled state rather than a second way to send a broken link.
                const cartId = this.cart && this.cart.id ? this.cart.id : null;
                if (!cartId) {
                    this.smsStatus = 'error';
                    this.smsStatusMessage = 'Set a cart product before sending the link.';
                    this.sendingLink = false;
                    return;
                }

                // Predefined message template
                const firstName = this.user.first_name || 'there';
                const message = `Hi ${firstName}!\nHere's the link to finish your signup:\nSmall favor — please use this link so I get credit on my end and keep my manager happy 😊 I'd really appreciate it!`;

                const cartLinkData = {
                    cartId: cartId,
                    identifire: Date.now().toString(), // Unique identifier
                    message: message,
                    phone: this.phoneNumber,
                    userId: this.user.id
                };

                if (this.viciAgent) {
                    cartLinkData.vici_agent = this.viciAgent;
                }

                await this.$http.post('/call-center/send-cart-link', cartLinkData);

                // Show success message below button
                this.smsStatus = 'success';
                this.smsStatusMessage = 'Message sent successfully!';
            } catch (error) {
                console.error('Error sending cart link:', error);
                this.smsStatus = 'error';
                this.smsStatusMessage = 'Failed to send message. Please try again.';
            } finally {
                // Reasoning: Reset sending state
                this.sendingLink = false;
            }
        }
    }
}
</script>

<style scoped>
/* Material Design inspired styles */
.user-details-container {
    max-width: 1400px;
    /* Increased max-width to utilize more screen space */
    margin: 0 auto;
    padding: 20px;
    position: relative;
    font-family: 'Roboto', Arial, sans-serif;
}

.refresh-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.btn-refresh {
    display: flex;
    align-items: center;
}

/* Main Content Layout - Two Column Grid */
.main-content {
    display: grid;
    grid-template-columns: 1fr 400px;
    /* Left column flexible, right column fixed 400px */
    gap: 30px;
    align-items: start;
}

/* Left Column Styles */
.left-column {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

/* Right Column Styles */
.right-column {
    position: sticky;
    top: 20px;
    /* Keeps pricing panel visible when scrolling */
}

.pricing-panel {
    max-height: calc(100vh - 100px);
    overflow-y: auto;
}

.pricing-section {
    margin-bottom: 30px;
}

.pricing-section:last-child {
    margin-bottom: 0;
}

.pricing-note {
    font-size: 0.8em;
    color: #666;
    font-style: italic;
}

/* Responsive Design */
@media (max-width: 1200px) {
    .main-content {
        grid-template-columns: 1fr 350px;
        gap: 20px;
    }

    .user-details-container {
        max-width: 1200px;
    }
}

@media (max-width: 992px) {
    .main-content {
        grid-template-columns: 1fr;
        gap: 20px;
    }

    .right-column {
        position: static;
    }

    .user-details-container {
        max-width: 800px;
    }
}

@media (max-width: 768px) {
    .user-details-container {
        padding: 15px;
    }

    .refresh-container {
        flex-direction: column;
        gap: 15px;
        align-items: stretch;
    }

    .main-content {
        gap: 15px;
    }
}

.loading,
.error,
.not-found {
    text-align: center;
    padding: 20px;
}

.md-loader {
    border: 4px solid rgba(0, 0, 0, 0.1);
    border-radius: 50%;
    border-top: 4px solid #3498db;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 15px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.invalid {
    border-color: red !important;
}

/* Card Styles */
.md-card {
    background-color: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin-bottom: 0;
    /* Remove bottom margin since we're using gap in grid */
    transition: box-shadow 0.3s ease;
}

.md-card:hover {
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.15);
}

.md-card-title {
    font-size: 1.5rem;
    font-weight: 500;
    color: #333;
    margin-top: 0;
    margin-bottom: 24px;
    text-align: center;
}

.md-card-subtitle {
    font-size: 1.2rem;
    font-weight: 500;
    color: #555;
    margin-top: 0;
    margin-bottom: 16px;
}

.md-card-section {
    border-top: 1px solid #eee;
    padding-top: 20px;
    margin-top: 20px;
}

.md-card-error {
    background-color: #ffebee;
    color: #d32f2f;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.md-card-selected {
    border: 2px solid #2196F3;
    background-color: #E3F2FD;
}

/* Alert Styles */
.md-alert {
    padding: 16px;
    border-radius: 4px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
}

.md-alert i {
    margin-right: 12px;
    font-size: 24px;
}

.md-alert-success {
    background-color: #E8F5E9;
    color: #2E7D32;
    border-left: 4px solid #4CAF50;
}

.md-alert-error {
    background-color: #ffebee;
    color: #d32f2f;
    border-left: 4px solid #f44336;
}

/* Panel Styles */
.md-panel {
    background-color: #f5f5f5;
    border-radius: 4px;
    padding: 16px;
    margin-bottom: 16px;
}

.md-panel-warning {
    background-color: #FFF8E1;
    border-left: 4px solid #FFC107;
}

.user-info {
    margin-bottom: 20px;
}

.info-row {
    display: flex;
    padding: 12px 0;
    border-bottom: 1px solid #eee;
    align-items: flex-start;
}

.info-row:last-child {
    border-bottom: none;
}

/* The first charge group ends above the card row, so its last row keeps the divider. */
.charge-group:not(:last-child) > .info-row:last-child {
    border-bottom: 1px solid #eee;
}

.label {
    font-weight: 500;
    min-width: 150px;
    width: 150px;
    color: #555;
    flex-shrink: 0;
}

.value {
    flex: 1;
    color: #333;
    word-break: break-word;
}

.actions {
    display: flex;
    justify-content: space-between;
    margin-top: 24px;
}

/* Button Styles */
.md-btn {
    display: inline-flex;
    align-items: center;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    font-size: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: background-color 0.2s, box-shadow 0.2s;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    text-decoration: none;
}

.md-btn i {
    margin-right: 6px;
}

.md-btn:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.md-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    box-shadow: none;
}

.md-btn-primary {
    background-color: #2196F3;
    color: white;
}

.md-btn-secondary {
    background-color: #757575;
    color: white;
}

.md-btn-success {
    background-color: #4CAF50;
    color: white;
}

.md-btn-info {
    background-color: #00BCD4;
    color: white;
}

/* Cart Actions */
.cart-actions {
    display: flex;
    justify-content: left;
    margin-top: 15px;
    gap: 30px;
}

@media (max-width: 600px) {
    .cart-actions {
        flex-direction: column;
    }
}

/* SMS Status Message */
.sms-status {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 15px;
    padding: 12px 16px;
    border-radius: 4px;
    font-size: 14px;
    font-weight: 500;
}

.sms-status i {
    font-size: 20px;
}

.sms-success {
    background-color: #E8F5E9;
    color: #2E7D32;
    border-left: 4px solid #4CAF50;
}

.sms-error {
    background-color: #ffebee;
    color: #d32f2f;
    border-left: 4px solid #f44336;
}

/* Dialog Styles */
.dialog-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.md-dialog {
    background-color: white;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    display: flex;
    flex-direction: column;
    max-height: 90vh;
    overflow: hidden;
}

.md-dialog-header {
    padding: 20px 24px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.md-dialog-header h3 {
    margin: 0;
    font-weight: 500;
    font-size: 1.2rem;
    color: #333;
}

.md-dialog-close {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #757575;
    padding: 4px;
    border-radius: 50%;
    display: flex;
}

.md-dialog-close:hover {
    background-color: #f1f1f1;
}

.md-dialog-body {
    padding: 24px;
    overflow-y: auto;
}

.md-dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 16px 24px;
    border-top: 1px solid #eee;
}

/* Product Selection */
.product-selection {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
}

.product-option {
    flex: 1;
    padding: 20px;
    cursor: pointer;
    text-align: center;
    transition: all 0.2s;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.product-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 60px;
    height: 60px;
    background-color: #E3F2FD;
    border-radius: 50%;
    margin-bottom: 12px;
}

.product-icon i {
    font-size: 30px;
    color: #2196F3;
}

.product-name {
    font-weight: 500;
    margin-bottom: 8px;
    color: #333;
    font-size: 1.1rem;
}

.product-price {
    color: #4CAF50;
    font-weight: 500;
    font-size: 1.2rem;
}

/* Form Styles */
.md-form-group {
    margin-bottom: 20px;
}

.md-form-group label {
    display: block;
    margin-bottom: 8px;
    font-weight: 500;
    color: #555;
    font-size: 0.9rem;
}

.md-input-container {
    position: relative;
}

.md-input-icon {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    color: #757575;
}

.md-input,
.md-textarea {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
    transition: border-color 0.2s, box-shadow 0.2s;
    box-sizing: border-box;
}

.md-input {
    padding-left: 40px;
}

.md-input:focus,
.md-textarea:focus {
    outline: none;
    border-color: #2196F3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.2);
}

.md-textarea {
    resize: vertical;
    min-height: 80px;
}

/* Order message */
.order-exists {
    display: flex;
    align-items: flex-start;
}

.order-exists i {
    font-size: 24px;
    margin-right: 12px;
    margin-top: 2px;
}

select {
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    padding: 10px 40px 10px 12px;
    border: 1px solid #ccc;
    border-radius: 8px;
    background-color: #fff;
    background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23666' d='M6 8L0 0h12z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 12px 8px;
    font-size: 16px;
    color: #333;
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    width: 100%;
    box-sizing: border-box;
}

select:focus {
    border-color: #66afe9;
    box-shadow: 0 0 4px rgba(102, 175, 233, 0.6);
    outline: none;
}

.tf_textfield-3.w-input {
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 16px;
    box-sizing: border-box;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.tf_textfield-3.w-input:focus {
    border-color: #66afe9;
    box-shadow: 0 0 4px rgba(102, 175, 233, 0.6);
    outline: none;
}

.md-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
    font-family: 'Roboto', sans-serif;
}

.md-table th,
.md-table td {
    border-bottom: 1px solid #e0e0e0;
    padding: 12px 16px;
    text-align: left;
}

.md-table th {
    background-color: #f5f5f5;
    color: #333;
    font-weight: 500;
}

.md-table tr:hover {
    background-color: #f9f9f9;
}

/* Card element styles for payment */
.cart-empty {
    margin: 0 0 8px;
    font-size: 13px;
    color: #5f6368;
}

.addon-group {
    margin-bottom: 10px;
}

.addon-group-name {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .04em;
    color: #5f6368;
    margin-bottom: 3px;
}

.addon-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.addon-pick {
    flex: 1;
    font-size: 13px;
}

.addon-total {
    margin-top: 6px;
    font-size: 12px;
    font-weight: 600;
}

.price-breakdown {
    font-size: 11px;
    color: #5f6368;
}

.free-addon-note {
    margin-top: 4px;
    font-size: 11px;
    color: #5f6368;
    line-height: 1.35;
}

.pricing-includes {
    margin: 0 0 8px;
    font-size: 12px;
    color: #5f6368;
}

.ns-card-element {
    padding: 10px 12px;
    border: 1px solid #ccc;
    border-radius: 6px;
    background-color: #fff;
}

/* Additional responsive improvements */
@media (max-width: 480px) {
    .md-card {
        padding: 16px;
    }

    .info-row {
        flex-direction: column;
        gap: 5px;
    }

    .label {
        min-width: auto;
        width: auto;
        font-weight: 600;
    }

    .md-table th,
    .md-table td {
        padding: 8px 12px;
        font-size: 14px;
    }

    .product-selection {
        flex-direction: column;
        gap: 10px;
    }
}

.current-product {
    margin-bottom: 16px;
}

.current-product:last-child {
    margin-bottom: 0;
}

/* Error message styles */
.error-message {
    width: 100%;
    margin-top: 10px;
}

.error-message p {
    margin: 0;
}

.coupon-input-container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 5px;
}

.remove-coupon-btn {
    background-color: #f44336;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background-color 0.2s ease;
    color: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.remove-coupon-btn:hover {
    background-color: #d32f2f;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.remove-coupon-btn i {
    font-size: 18px;
    margin: 0;
}
</style>
